import TemplateTable, { ColumnDefCustom } from "@/shared/components/ui/table/table.tsx";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui/components/ui/card.tsx";
import { getAlerts, IGetAlertsInput } from "@/alerts/api/requests/machine-alerts.requests.ts";
import { useFilterStore } from "@/shared/stores/useFilterStore.ts";
import { QUERY_KEYS } from "@/alerts/api/config.ts";
import { formatDate } from "@/shared/lib/i18n/formatters.ts";
import { alertStatusLabel } from "@/machines/utils/alert-status-label.ts";
import { Button } from "@/ui/components/ui/button.tsx";
import { useDateTimeRangeStore } from "@/shared/stores/useDateTimeRangeStore.ts";
import { AlertStatus, Alert } from "@/alerts/types/machine-alert.ts";
import { Badge } from "@/ui/components/ui/badge.tsx";
import { useAlertDetailStore } from "@/alerts/stores/use-alert-detail-store";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueries } from "@tanstack/react-query";
import { ApiResponse } from "@/shared/types/api.ts";
import AlertsFilterBar, { AlertsFilterState } from "@/machines/components/table/alerts-filter-bar.tsx";

interface AggregatedAlertsTableProps {
  device_mxids?: string[];
  setSelectedTime?: (time: number, source: string) => void;
  shouldModalOpen: boolean;
}

const AggregatedAlertsTable = ({
  device_mxids = [],
  setSelectedTime,
  shouldModalOpen = false
}: AggregatedAlertsTableProps) => {
  const { t } = useTranslation("machines");
  const { getFilters, setFilters } = useFilterStore();
  const { value } = useDateTimeRangeStore();
  const openRealAlert = useAlertDetailStore((s) => s.openReal);

  // Use a single filter key for aggregated alerts
  const filterKey = `${QUERY_KEYS.ALERTS}/aggregated`;
  const filters = getFilters(filterKey) as { page?: number; itemsPerPage?: number };
  const [alertFilters, setAlertFilters] = useState<AlertsFilterState>({
    ordering: "-timestamp",
  });

  const handleFilterChange = (nextFilters: AlertsFilterState) => {
    setAlertFilters(nextFilters);
    setFilters(filterKey, { page: 1 });
  };

  const handleClearFilters = () => {
    setAlertFilters({ ordering: "-timestamp" });
    setFilters(filterKey, { page: 1 });
  };

  const alertQueries = useQueries({
    queries: device_mxids.map((device_mxid): { queryKey: unknown[]; queryFn: () => Promise<ApiResponse<Alert[]>>; enabled: boolean } => ({
      queryKey: [QUERY_KEYS.ALERTS, { device_mxid, start_time: value.fromDate, end_time: value.toDate, ...alertFilters }],
      queryFn: () => getAlerts({
        device_mxid,
        start_time: value.fromDate,
        end_time: value.toDate,
        page: 1,
        itemsPerPage: 1000,
        ...alertFilters,
      } as IGetAlertsInput),
      enabled: device_mxids.length > 0,
    })),
  });

  const aggregatedData = useMemo(() => {
    const allAlerts: Alert[] = [];
    let isLoading = false;
    let hasError = false;

    alertQueries.forEach((query) => {
      if (query.status === "pending") isLoading = true;
      if (query.status === "error") hasError = true;
      if (query.data?.data) allAlerts.push(...query.data.data);
    });

    allAlerts.sort((a, b) => {
      const diff = new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
      return alertFilters.ordering === "timestamp" ? diff : -diff;
    });

    const page = filters?.page ?? 1;
    const itemsPerPage = filters?.itemsPerPage ?? 10;
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedAlerts = allAlerts.slice(startIndex, endIndex);

    return {
      alerts: paginatedAlerts,
      total: allAlerts.length,
      isLoading,
      hasError,
      page,
      itemsPerPage,
    };
  }, [alertQueries, filters, alertFilters.ordering]);

  const alertsColumns: ColumnDefCustom<Alert>[] = [
    {
      header: t("alertsTable.columns.dateTime"),
      accessorKey: "timestamp",
      cell: ({ row }) => formatDate(new Date(row.original.timestamp), "P HH:mm:ss"),
    },
    {
      header: t("alertsTable.columns.machine"),
      accessorKey: "device_mxid",
    },
    {
      header: t("alertsTable.columns.eventType"),
      accessorKey: "type",
    },
    {
      header: t("alertsTable.columns.category"),
      accessorKey: "origin",
    },
    {
      header: t("alertsTable.columns.status"),
      accessorKey: "status",
      cell: ({ row }) => {
        const status = row.original.status as AlertStatus;

        const variantMap: Record<AlertStatus, "default" | "secondary" | "destructive" | "outline" | null | undefined> = {
          [AlertStatus.Pending]: "secondary",
          [AlertStatus.Resolved]: "default",
          [AlertStatus.Ignored]: "outline",
        };

        return (
          <Badge variant={variantMap[status]}>
            {alertStatusLabel(status, t)}
          </Badge>
        );
      },
    },
    {
      header: t("alertsTable.columns.reported"),
      accessorKey: "timestamp",
      id: "reported",
      cell: ({ row }) => formatDate(new Date(row.original.timestamp)),
    },
    {
      header: "",
      id: "viewShow",
      cell: ({ row }) => {
        const handleClick = () => {
          if (!row.original.id) return;
          if (shouldModalOpen) openRealAlert(row.original.id);

          // Only interact with timeline if setSelectedTime is provided
          if (setSelectedTime) {
            const timestamp = new Date(row.original.timestamp).getTime() + 100;
            setSelectedTime(timestamp, "external");
            // Scroll to the element with id="main-preview"
            const previewElement = document.getElementById("main-preview");
            if (previewElement) {
              previewElement.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          }
        };
        return (
          <Button variant="outline" size="sm" onClick={handleClick}>
            {t("alertsTable.view")}
          </Button>
        );
      },
      enableSorting: false,
    },
  ];

  // Create custom pagination metadata matching the API response format
  const paginationMetadata = {
    total: aggregatedData.total,
    page: aggregatedData.page,
    itemsPerPage: aggregatedData.itemsPerPage,
    totalPages: Math.ceil(aggregatedData.total / aggregatedData.itemsPerPage),
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("alertsTable.aggregatedTitle")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <AlertsFilterBar
          value={alertFilters}
          onChange={handleFilterChange}
          onClear={handleClearFilters}
        />
        <TemplateTable
          items={aggregatedData.alerts}
          columns={alertsColumns}
          pagination={paginationMetadata}
          filterKey={filterKey}
          isLoading={aggregatedData.isLoading}
        />
      </CardContent>
    </Card>
  );
};

export default AggregatedAlertsTable;
